// Contract 29 §7/§9 — the pure stepper arithmetic. No Vue, no Pinia, no `shared/http`.

import {
  FIRST_ONBOARDING_STEP,
  ONBOARDING_STEPS,
  type OnboardingStepKey,
} from '../models/onboarding.types'

export function isOnboardingStepKey(value: unknown): value is OnboardingStepKey {
  return (
    typeof value === 'string' &&
    (ONBOARDING_STEPS.includes(value as (typeof ONBOARDING_STEPS)[number]) ||
      value in LEGACY_STEP_ALIASES)
  )
}

const LEGACY_STEP_ALIASES: Record<string, (typeof ONBOARDING_STEPS)[number]> = {
  workspace: 'who-brand',
  'found-brand': 'settings',
  'business-type': 'settings',
  connect: 'connections',
  authentications: 'connections',
}

export function normalizeOnboardingStep(value: unknown): (typeof ONBOARDING_STEPS)[number] {
  return typeof value === 'string' && value in LEGACY_STEP_ALIASES
    ? (LEGACY_STEP_ALIASES[value] ?? ONBOARDING_STEPS[0])
    : (value as (typeof ONBOARDING_STEPS)[number])
}

/** 1-based index, matching the legacy `layoutStore.onboardingStep` numbering. */
export function stepNumber(key: OnboardingStepKey): number {
  const normalized = normalizeOnboardingStep(key)
  return ONBOARDING_STEPS.indexOf(normalized) + 1
}

export function stepAt(number: number): OnboardingStepKey {
  const clamped = Math.min(Math.max(number, 1), ONBOARDING_STEPS.length)
  return ONBOARDING_STEPS[clamped - 1] ?? FIRST_ONBOARDING_STEP
}

/**
 * "You may click back to a COMPLETED step, never forward" (legacy `layoutStore.ts:530-532`).
 * `furthest` is the highest step the user has actually reached.
 */
export function canRevisit(key: OnboardingStepKey, furthest: number): boolean {
  return stepNumber(key) <= furthest
}

export function nextStepKey(key: OnboardingStepKey): OnboardingStepKey {
  return stepAt(stepNumber(key) + 1)
}

export function previousStepKey(key: OnboardingStepKey): OnboardingStepKey {
  return stepAt(stepNumber(key) - 1)
}

export function isLastStep(key: OnboardingStepKey): boolean {
  return stepNumber(key) === ONBOARDING_STEPS.length
}

/**
 * `mustOnboard` (§9) — an ENTITLEMENT state, not a permission.
 *
 * Legacy fired `activeOnboard()` on `workspaces.length === 0` and nothing else; the two extra
 * conjuncts are the deliberate v4 additions the contract specifies: `ready` because the predicate
 * is now read by a guard rather than by post-boot imperative code, and `!onlyDashboard` because a
 * dashboard-only user legitimately has zero workspaces and was previously spared only by a
 * separate router branch.
 */
export function mustOnboard(input: {
  status: string
  workspaceCount: number
  onlyDashboard: boolean
}): boolean {
  return input.status === 'ready' && input.workspaceCount === 0 && !input.onlyDashboard
}
